/**
 * The virtualised evidence map (UX-SPEC §6): a window of paragraph rows between two spacers, row heights estimated and
 * corrected on render, the route contract `#/read?p&ch&hs&sp`, the evidence cursor and the keyboard model.
 *
 * Scrolling is the document's (the shell's sticky band sits above). Only the window is ever rendered or computed; all
 * positions come from `Geometry` (prefix sums of per-paragraph heights). After every render the rows are measured, the
 * spacers follow, and the scroll position is corrected so that an anchor paragraph (the pinned arrival target, else the
 * first visible one) does not move on screen.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { CHANNEL_IDS } from '../../engine/channels/types.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { keptBestParas } from '../bench/state.ts';
import { readerWindow } from '../components/HeatStrip.tsx';
import { focus } from '../focus.ts';
import { setFocus } from '../focus.ts';
import { fmtInt } from '../format.ts';
import { channelIndex, derive } from '../model.ts';
import { route, setParams } from '../router.ts';
import { READER_EXPLAIN } from '../strings-explain.ts';
import { ANNOUNCE, READER } from '../strings-reader.ts';
import { ChapterRail } from './ChapterRail.tsx';
import { type ActivePoint, Inspector, fmtWeight, spansAt } from './Inspector.tsx';
import { Row } from './Row.tsx';
import { type Layer, Toolbar } from './Toolbar.tsx';
import { bracketsFor, flagWord, nextStart, prevStart, sortedFlagged } from './flagged.ts';
import { Geometry, estimateHeights, windowFor } from './geometry.ts';
import { slotTable, spansOfParagraph } from './pieces.ts';
import { useFind } from './useFind.ts';
import {
	applySpacers,
	idsOf,
	lowerBound,
	parseRange,
	pieceAt,
	readMetrics,
	readPref,
	segmentsOf,
	stickyBottom,
	tupleOf,
	writePref,
} from './util.ts';

const OVERSCAN_VIEWPORTS = 1.5;
const MAX_ROWS = 150;
const MAX_MARKS = 3600;
const PIN_MS = 1500;
const NO_FOCUS_SLOTS = new Int8Array(CHANNEL_IDS.length).fill(-1);

/* ---------------- the reader ---------------- */

interface View {
	v0: number;
	v1: number;
	line: number;
}

export function VirtualReader({ vm }: { vm: ViewModel }) {
	const r = route.value;
	const n = vm.paras.count;
	const dv = derive(vm);
	const focused = focus.value;

	/* ---- geometry and window ---- */
	const geo = useRef<Geometry | null>(null);
	const metricsRef = useRef({ font: 17, line: 28.9 });
	if (!geo.current || geo.current.n !== n) {
		const w = Math.min(680, Math.max(240, window.innerWidth - 160));
		geo.current = new Geometry(n, estimateHeights(vm, { width: w, ...metricsRef.current }));
		geo.current.width = w;
	}
	const initial = useRef(true);
	const [win, setWinState] = useState(() => {
		const g = geo.current as Geometry;
		const p0 = Math.min(n - 1, Math.max(0, Number(r.params.get('p')) || 0));
		const vh = window.innerHeight;
		return windowFor(
			vm,
			g,
			g.at(g.top(p0)),
			g.at(g.top(p0) + vh),
			vh * OVERSCAN_VIEWPORTS,
			MAX_ROWS,
			MAX_MARKS
		);
	});
	const winRef = useRef(win);
	const setWin = useCallback((w: { start: number; end: number }) => {
		winRef.current = w;
		setWinState(w);
	}, []);
	const renderedWin = useRef(win);

	const regionRef = useRef<HTMLDivElement>(null);
	const rootRef = useRef<HTMLDivElement>(null);
	const topRef = useRef<HTMLDivElement>(null);
	const botRef = useRef<HTMLDivElement>(null);
	const closeRef = useRef<HTMLButtonElement>(null);

	/* ---- UI state ---- */
	const [view, setView] = useState<View>({ v0: 0, v1: 0, line: 0 });
	const viewRef = useRef(view);
	const [layer, setLayer] = useState<Layer>(focused.length ? 'focused' : 'all');
	const [active, setActive] = useState<ActivePoint | null>(null);
	const [cursor, setCursor] = useState<{ span: number; p: number; a: number } | null>(null);
	const [target, setTarget] = useState<{ p: number } | null>(null);
	const [live, setLive] = useState('');
	const [shortcuts, setShortcutsState] = useState(() =>
		readPref('provenance.reader.shortcuts', true)
	);
	const [toolsOpen, setToolsOpen] = useState(false);
	const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(null);
	const [hs, setHs] = useState<[number, number] | null>(() => parseRange(r.params.get('hs')));

	const shortcutsRef = useRef(shortcuts);
	shortcutsRef.current = shortcuts;
	const layerRef = useRef(layer);
	layerRef.current = layer;
	const focusedRef = useRef(focused);
	focusedRef.current = focused;
	const cursorRef = useRef(cursor);
	cursorRef.current = cursor;
	const activeRef = useRef(active);
	activeRef.current = active;

	const pinRef = useRef<{ p: number; vpTop: number; until: number } | null>(null);
	/** Where this component last put the scroll position: a scroll event elsewhere is the user's, and releases the pin. */
	const ownY = useRef(0);
	const scrollToY = useCallback((y: number) => {
		window.scrollTo({ top: y, behavior: 'instant' });
		ownY.current = window.scrollY;
	}, []);
	const focusPending = useRef<number | null>(null);
	const pendingReveal = useRef<{ p: number; sel: string } | null>(null);
	const returnFocus = useRef<HTMLElement | null>(null);
	const lastSeen = useRef<string>('');
	const pTimer = useRef(0);
	const flagIdx = useRef<{ key: string; i: number } | null>(null);
	const gState = useRef<{ digits: string; timer: number } | null>(null);
	const tipTimer = useRef(0);
	const tipEl = useRef<Element | null>(null);

	/* ---- layer and focus ---- */
	const prevFocusLen = useRef(focused.length);
	useEffect(() => {
		if (prevFocusLen.current === 0 && focused.length > 0) setLayer('focused');
		if (prevFocusLen.current > 0 && focused.length === 0) setLayer('all');
		prevFocusLen.current = focused.length;
	}, [focused.length]);

	const slotOf: Int8Array | null = useMemo(
		() =>
			layer === 'all'
				? null
				: focused.length === 0
					? NO_FOCUS_SLOTS
					: slotTable(focused.map(channelIndex)),
		[layer, focused]
	);
	const layerKey = `${layer}:${focused.join(',')}`;
	const slotOfRef = useRef(slotOf);
	slotOfRef.current = slotOf;

	const cellsByRow = useMemo(() => {
		if (layer !== 'focused' || focused.length === 0) return null;
		const list = sortedFlagged(vm, focused);
		return bracketsFor(list, new Map(focused.map((id, i) => [id, i])), win.start, win.end);
	}, [vm, layer, focused, win]);

	/* ---- measuring and anchoring ---- */
	/** Set when something besides the width changed every row's height (web fonts arrived): forget the measurements. */
	const remeasureAll = useRef(false);

	const settle = useCallback(() => {
		const g = geo.current;
		const el = regionRef.current;
		const top = topRef.current;
		if (!g || !el || !top) return;
		const w = renderedWin.current;
		applySpacers(g, w, top, botRef.current);
		const sb = stickyBottom();
		const rows = Array.from(el.querySelectorAll<HTMLElement>('.rd-row'));
		// the anchor is a rendered row: the pinned arrival target, else the first row that reaches below the sticky band
		const pin = pinRef.current && pinRef.current.until > performance.now() ? pinRef.current : null;
		let anchor = pin ? rows.find((r) => Number(r.dataset.p) === pin.p) : undefined;
		if (!anchor) {
			pinRef.current = null;
			anchor = rows.find((r) => r.getBoundingClientRect().bottom > sb + 1);
		}
		const desired = anchor ? (pin ? pin.vpTop : anchor.getBoundingClientRect().top) : 0;
		const m = readMetrics(el);
		if (m) metricsRef.current = m;
		const body = el.querySelector('.rd-body') as HTMLElement | null;
		const bw = body ? body.clientWidth : 0;
		if (bw > 0 && (Math.abs(bw - g.width) > 1 || remeasureAll.current)) {
			remeasureAll.current = false;
			g.reset(estimateHeights(vm, { width: bw, ...metricsRef.current }), bw);
		}
		for (const row of rows) {
			const p = Number(row.dataset.p);
			if (Number.isFinite(p)) g.measure(p, row.getBoundingClientRect().height);
		}
		g.recalibrate();
		g.rebuild();
		applySpacers(g, w, top, botRef.current);
		if (anchor) {
			const d = anchor.getBoundingClientRect().top - desired;
			if (Math.abs(d) > 0.5) scrollToY(window.scrollY + d);
		}
	}, [vm, scrollToY]);

	/* ---- what is visible ---- */
	const writeP = useCallback((p: number) => {
		clearTimeout(pTimer.current);
		const at = (): string => {
			const q = route.value.params;
			return tupleOf(q.get('p'), q.get('hs'), q.get('sp'));
		};
		const scheduledFor = at();
		pTimer.current = window.setTimeout(() => {
			if (route.value.view !== 'read') return;
			// a navigation since this write was scheduled (a strip click, Back) wins over the position it saw
			if (at() !== scheduledFor) return;
			const cur = route.value.params.get('p');
			if (cur === String(p)) return;
			lastSeen.current = tupleOf(
				String(p),
				route.value.params.get('hs'),
				route.value.params.get('sp')
			);
			setParams({ p: String(p) });
		}, 250);
	}, []);

	const readerWindowTimer = useRef(0);
	const lastWindow = useRef({ a: -1, b: -1 });

	const sync = useCallback(() => {
		const g = geo.current;
		const top = topRef.current;
		if (!g || !top || g.n === 0) return;
		const origin = top.getBoundingClientRect().top;
		const sb = stickyBottom();
		const vh = window.innerHeight;
		const yTop = sb - origin;
		const yBot = vh - origin;
		const v0 = g.at(yTop);
		const v1 = g.at(yBot);
		const lineY = sb + (vh - sb) / 3;
		const line = g.at(lineY - origin + 12);
		// keep the window around what is visible (hysteresis: re-window when less than half a viewport is left)
		const w = winRef.current;
		const lo = g.at(yTop - 0.5 * vh);
		const hi = g.at(yBot + 0.5 * vh);
		if (w.start > lo || w.end <= hi) {
			setWin(windowFor(vm, g, v0, v1, vh * OVERSCAN_VIEWPORTS, MAX_ROWS, MAX_MARKS));
		}
		const cur = viewRef.current;
		if (cur.v0 !== v0 || cur.v1 !== v1 || cur.line !== line) {
			const nv = { v0, v1, line };
			viewRef.current = nv;
			setView(nv);
			if (lastWindow.current.a !== v0 || lastWindow.current.b !== v1) {
				lastWindow.current = { a: v0, b: v1 };
				clearTimeout(readerWindowTimer.current);
				readerWindowTimer.current = window.setTimeout(() => {
					readerWindow.value = {
						a: vm.paras.charStart[v0] as number,
						b: vm.paras.charEnd[v1] as number,
					};
				}, 60);
			}
			if (cur.line !== line) writeP(line);
		}
	}, [vm, setWin, writeP]);

	/* ---- arriving at a paragraph (UX-MAP-14) ---- */
	const arrive = useCallback(
		(p0: number, opts: { focus?: boolean; mark?: boolean } = {}) => {
			const g = geo.current;
			const top = topRef.current;
			if (!g || !top) return;
			const p = Math.max(0, Math.min(n - 1, p0));
			const vh = window.innerHeight;
			const sb = stickyBottom();
			const vpTop = sb + (vh - sb) / 3;
			pinRef.current = { p, vpTop, until: performance.now() + PIN_MS };
			const docOrigin = top.getBoundingClientRect().top + window.scrollY;
			const y = Math.max(0, docOrigin + g.top(p) - vpTop);
			const yTop = g.top(p) - (vpTop - sb);
			const w = windowFor(
				vm,
				g,
				g.at(yTop),
				g.at(yTop + vh - sb),
				vh * OVERSCAN_VIEWPORTS,
				MAX_ROWS,
				MAX_MARKS
			);
			// the target must be inside the window whatever the trimming did
			if (p < w.start || p >= w.end) {
				w.start = Math.min(w.start, p);
				w.end = Math.max(w.end, p + 1);
			}
			setWin(w);
			scrollToY(y);
			if (opts.mark !== false) {
				setTarget(null);
				requestAnimationFrame(() => setTarget({ p }));
			}
			if (opts.focus !== false) focusPending.current = p;
		},
		[n, vm, setWin, scrollToY]
	);

	const say = useCallback((text: string) => {
		setLive('');
		requestAnimationFrame(() => setLive(text));
	}, []);

	/** Put the element matching `sel` in row `p` at the reading line (a third of the way down). */
	const revealIn = useCallback(
		(p: number, sel: string) => {
			const row = regionRef.current?.querySelector(`.rd-row[data-p="${p}"]`);
			const el = row?.querySelector(sel) as HTMLElement | null | undefined;
			if (!el) return false;
			const sb = stickyBottom();
			const vh = window.innerHeight;
			const rect = el.getBoundingClientRect();
			if (rect.top >= sb + 8 && rect.bottom <= vh - 8) return true;
			const lineY = sb + (vh - sb) / 3;
			pinRef.current = null;
			scrollToY(window.scrollY + rect.top - lineY);
			return true;
		},
		[scrollToY]
	);

	const reveal = useCallback(
		(p: number, sel: string) => {
			if (revealIn(p, sel)) return;
			pendingReveal.current = { p, sel };
			arrive(p, { focus: false, mark: false });
		},
		[arrive, revealIn]
	);

	/* ---- the evidence cursor ---- */
	const cursorTo = useCallback(
		(i: number) => {
			const s = vm.spans;
			const start = s.start[i] as number;
			const p = dv.paraOf(start);
			const pc = pieceAt(vm, p, start, slotOfRef.current);
			const a = pc ? pc.a : start;
			setCursor({ span: i, p, a });
			const c = s.channel[i] as number;
			const d = vm.channels[c];
			say(
				ANNOUNCE.evidence(
					CHANNEL_IDS[c] as string,
					d?.features[s.feature[i] as number] ?? '',
					fmtWeight(s.weight[i] as number),
					fmtInt(p + 1),
					fmtInt(n)
				)
			);
			reveal(p, `mark[data-a="${a}"]`);
		},
		[vm, dv, n, say, reveal]
	);

	const stepEvidence = useCallback(
		(dir: 1 | -1) => {
			const s = vm.spans;
			const f = focusedRef.current;
			const sl = slotOfRef.current;
			if (layerRef.current === 'focused' && f.length === 0) {
				say(READER.noFocusHint);
				return;
			}
			const accept = (i: number): boolean => !sl || (sl[s.channel[i] as number] as number) >= 0;
			const cur = cursorRef.current;
			let i: number;
			if (cur) i = cur.span + dir;
			else {
				const base = lowerBound(s.start, vm.paras.charStart[viewRef.current.line] as number);
				i = dir > 0 ? base : base - 1;
			}
			while (i >= 0 && i < s.count && !accept(i)) i += dir;
			if (i < 0 || i >= s.count) {
				say(ANNOUNCE.noMoreEvidence);
				return;
			}
			cursorTo(i);
		},
		[vm, say, cursorTo]
	);

	/* ---- flagged stretches (h / H) ---- */
	const stepFlagged = useCallback(
		(dir: 1 | -1) => {
			const f = layerRef.current === 'focused' ? focusedRef.current : [];
			if (layerRef.current === 'focused' && f.length === 0) {
				say(READER.noFocusHint);
				return;
			}
			const list = sortedFlagged(vm, f);
			if (list.length === 0) {
				say(ANNOUNCE.noFlagged);
				return;
			}
			const key = `${layerRef.current}:${f.join(',')}`;
			const line = viewRef.current.line;
			const cur = flagIdx.current;
			let i: number;
			if (cur && cur.key === key && list[cur.i] && (list[cur.i]?.paraStart as number) === line)
				i = cur.i + dir;
			else i = dir > 0 ? nextStart(list, line) : prevStart(list, line);
			const h = i >= 0 && i < list.length ? list[i] : undefined;
			if (!h) {
				say(dir > 0 ? ANNOUNCE.noMoreFlagged : ANNOUNCE.noMoreFlagged);
				return;
			}
			flagIdx.current = { key, i };
			setHs([h.paraStart, h.paraEnd]);
			lastSeen.current = tupleOf(String(h.paraStart), `${h.paraStart}-${h.paraEnd}`, null);
			setParams({ p: String(h.paraStart), hs: `${h.paraStart}-${h.paraEnd}`, sp: undefined });
			arrive(h.paraStart);
			say(ANNOUNCE.flagged(h.channel, flagWord(h), fmtInt(h.paraStart + 1), fmtInt(h.paraEnd)));
		},
		[vm, arrive, say]
	);

	/* ---- the inspector ---- */
	const openInspector = useCallback(
		(p: number, a: number, b: number, byKeyboard: boolean) => {
			const ids = spansOfParagraph(vm.spans, p);
			const spans = spansAt(vm, ids, a);
			returnFocus.current = document.activeElement as HTMLElement | null;
			setActive({ p, a, b, spans });
			if (spans.length > 0) {
				const last = spans.reduce(
					(m, i) => ((vm.spans.start[i] as number) > (vm.spans.start[m] as number) ? i : m),
					spans[0] as number
				);
				const cp = pieceAt(vm, p, a, slotOfRef.current);
				setCursor({ span: spans[0] as number, p, a: cp ? cp.a : a });
				lastSeen.current = tupleOf(
					route.value.params.get('p'),
					route.value.params.get('hs'),
					String(last)
				);
				setParams({ sp: String(last) });
			}
			if (byKeyboard) requestAnimationFrame(() => closeRef.current?.focus());
		},
		[vm]
	);

	const closeInspector = useCallback(() => {
		if (!activeRef.current) return;
		setActive(null);
		if (route.value.params.get('sp') !== null) {
			lastSeen.current = tupleOf(route.value.params.get('p'), route.value.params.get('hs'), null);
			setParams({ sp: undefined });
		}
		const back = returnFocus.current;
		requestAnimationFrame(() => {
			if (back && document.contains(back) && back !== document.body)
				back.focus({ preventScroll: true });
			else regionRef.current?.focus({ preventScroll: true });
		});
		say(ANNOUNCE.inspectorClosed);
	}, [say]);

	/* ---- jumping to a chapter ---- */
	const gotoChapter = useCallback(
		(i: number) => {
			const c = vm.chapters[i];
			if (!c) return;
			arrive(c.paraStart);
			say(ANNOUNCE.chapter(i + 1, c.title));
		},
		[vm, arrive, say]
	);

	/* ---- route: arrivals, sp, hs ---- */
	// biome-ignore lint/correctness/useExhaustiveDependencies: runs per route object; helpers are stable
	useEffect(() => {
		const pStr = r.params.get('p');
		const hsStr = r.params.get('hs');
		const spStr = r.params.get('sp');
		const tup = tupleOf(pStr, hsStr, spStr);
		const first = initial.current;
		initial.current = false;
		if (!first && tup === lastSeen.current) return;
		lastSeen.current = tup;
		clearTimeout(pTimer.current);
		const range = parseRange(hsStr);
		setHs(range);
		const sp = spStr !== null && /^\d+$/.test(spStr) ? Number(spStr) : -1;
		let p = pStr !== null && /^\d+$/.test(pStr) ? Number(pStr) : range ? range[0] : -1;
		let openSpan = -1;
		if (sp >= 0 && sp < vm.spans.count) {
			openSpan = sp;
			if (p < 0) p = dv.paraOf(vm.spans.start[sp] as number);
		}
		if (p >= 0) {
			arrive(p);
		} else if (first) {
			scrollToY(0);
			sync();
		}
		if (openSpan >= 0) {
			const pos = vm.spans.start[openSpan] as number;
			const pp = dv.paraOf(pos);
			const pc = pieceAt(vm, pp, pos, slotOfRef.current);
			const ids = spansOfParagraph(vm.spans, pp);
			setActive({
				p: pp,
				a: pc ? pc.a : pos,
				b: pc ? pc.b : pos + 1,
				spans: spansAt(vm, ids, pos),
			});
			setCursor({ span: openSpan, p: pp, a: pc ? pc.a : pos });
		} else if (!first && activeRef.current) {
			setActive(null);
		}
	}, [r]);

	// ch ↔ focus
	const routeCh = r.params.get('ch') ?? '';
	const chFirst = useRef(true);
	useEffect(() => {
		const first = chFirst.current;
		chFirst.current = false;
		const cur = focusedRef.current.join(',');
		if (routeCh === cur) return;
		if (first && routeCh === '') return; // the focus set elsewhere wins on arrival; the next effect writes it
		setFocus(idsOf(routeCh));
	}, [routeCh]);
	const focusKey = focused.join(',');
	// biome-ignore lint/correctness/useExhaustiveDependencies: runs when the focus changes; it reads the live signal, not the render's copy
	useEffect(() => {
		const current = route.value.params.get('ch') ?? '';
		const live = focus.value.join(',');
		if (current === live) return;
		lastSeen.current = tupleOf(
			route.value.params.get('p'),
			route.value.params.get('hs'),
			route.value.params.get('sp')
		);
		setParams({ ch: live || undefined });
	}, [focusKey]);

	/* ---- effects: render commit, scroll, resize, fonts ---- */
	useLayoutEffect(() => {
		renderedWin.current = win;
		settle();
		const fp = focusPending.current;
		const rev = pendingReveal.current;
		if (rev) {
			pendingReveal.current = null;
			revealIn(rev.p, rev.sel);
		}
		if (fp !== null) {
			focusPending.current = null;
			const row = regionRef.current?.querySelector(`.rd-row[data-p="${fp}"]`) as HTMLElement | null;
			row?.focus({ preventScroll: true });
		}
		sync();
	}, [win, settle, sync, revealIn]);

	useEffect(() => {
		let frame = 0;
		const onScroll = () => {
			if (frame) return;
			frame = requestAnimationFrame(() => {
				frame = 0;
				if (Math.abs(window.scrollY - ownY.current) > 2) pinRef.current = null;
				ownY.current = window.scrollY;
				sync();
			});
		};
		const release = () => {
			pinRef.current = null;
		};
		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('wheel', release, { passive: true });
		window.addEventListener('touchstart', release, { passive: true });
		window.addEventListener('keydown', release);
		const onEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && !e.defaultPrevented && activeRef.current) closeInspector();
		};
		window.addEventListener('keydown', onEscape);
		const el = regionRef.current;
		// resizes are handled on the next frame: adjusting the spacers inside the callback would loop the observer
		let roFrame = 0;
		const ro =
			typeof ResizeObserver === 'function' && el
				? new ResizeObserver(() => {
						if (roFrame) return;
						roFrame = requestAnimationFrame(() => {
							roFrame = 0;
							settle();
							sync();
						});
					})
				: null;
		if (el) ro?.observe(el);
		const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
		const onFonts = () => {
			remeasureAll.current = true;
			settle();
			sync();
		};
		fonts?.addEventListener?.('loadingdone', onFonts);
		sync();
		return () => {
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('wheel', release);
			window.removeEventListener('touchstart', release);
			window.removeEventListener('keydown', release);
			window.removeEventListener('keydown', onEscape);
			ro?.disconnect();
			if (roFrame) cancelAnimationFrame(roFrame);
			fonts?.removeEventListener?.('loadingdone', onFonts);
			if (frame) cancelAnimationFrame(frame);
		};
	}, [sync, settle, closeInspector]);

	// the sticky band's and the toolbar's heights feed the sticky offsets of the toolbar, rail and inspector (CSS
	// variables, set through the CSSOM); both can change with the width, so they are observed
	useEffect(() => {
		const root = rootRef.current;
		const bar = root?.querySelector('.rd-toolbar');
		const band = document.querySelector('.stickytop');
		if (!root || !bar || typeof ResizeObserver !== 'function') return;
		const set = () => {
			root.style.setProperty('--rd-toolbar-h', `${bar.getBoundingClientRect().height}px`);
			if (band) root.style.setProperty('--rd-band-h', `${band.getBoundingClientRect().height}px`);
		};
		set();
		let frame = 0;
		const ro = new ResizeObserver(() => {
			if (frame) return;
			frame = requestAnimationFrame(() => {
				frame = 0;
				set();
			});
		});
		ro.observe(bar);
		if (band) ro.observe(band);
		return () => {
			ro.disconnect();
			if (frame) cancelAnimationFrame(frame);
		};
	}, []);

	useEffect(
		() => () => {
			clearTimeout(pTimer.current);
			clearTimeout(readerWindowTimer.current);
			clearTimeout(tipTimer.current);
			readerWindow.value = null;
		},
		[]
	);

	/* ---- pointer: click a mark, hover tooltip ---- */
	const markOf = (t: EventTarget | null): HTMLElement | null =>
		t instanceof Element ? (t.closest('mark.pc') as HTMLElement | null) : null;

	const onClick = (e: MouseEvent) => {
		const m = markOf(e.target);
		if (!m) return;
		const row = m.closest('.rd-row') as HTMLElement | null;
		const p = Number(row?.dataset.p);
		const a = Number(m.dataset.a);
		const b = Number(m.dataset.b);
		if (!Number.isFinite(p) || !Number.isFinite(a)) return;
		setTip(null);
		openInspector(p, a, b, false);
	};

	const onPointerOver = (e: PointerEvent) => {
		if (e.pointerType !== 'mouse') return;
		const m = markOf(e.target);
		if (m === tipEl.current) return;
		tipEl.current = m;
		clearTimeout(tipTimer.current);
		setTip(null);
		if (!m) return;
		const x = e.clientX;
		const y = e.clientY;
		tipTimer.current = window.setTimeout(() => {
			// one entry per channel: its index and the first span of it under the pointer
			const firstOf = new Map<number, number>();
			for (const s of (m.dataset.spans ?? '').split(',')) {
				if (!s) continue;
				const i = Number(s);
				const c = vm.spans.channel[i] as number;
				if (!firstOf.has(c)) firstOf.set(c, i);
			}
			if (firstOf.size === 0) return;
			const lines: string[] = [];
			for (const [c, i] of firstOf) {
				if (lines.length === 3) break;
				const d = vm.channels[c];
				const feature = d?.features[vm.spans.feature[i] as number] ?? '';
				const line = READER_EXPLAIN.tip(CHANNEL_IDS[c] as string, d?.name ?? '', feature);
				lines.push(line.replace(/( · )+$/, ''));
			}
			if (firstOf.size > 3) lines.push(READER_EXPLAIN.tipMany(String(firstOf.size)));
			setTip({ text: lines.join('\n'), x, y });
		}, 300);
	};
	const onPointerLeave = () => {
		tipEl.current = null;
		clearTimeout(tipTimer.current);
		setTip(null);
	};

	/* ---- keyboard ---- */
	const focusedRowP = (): number | null => {
		const a = document.activeElement;
		const row =
			a && regionRef.current?.contains(a) ? (a.closest('.rd-row') as HTMLElement | null) : null;
		return row ? Number(row.dataset.p) : null;
	};

	const moveParagraph = (dir: 1 | -1) => {
		const cur = focusedRowP();
		const p = cur === null ? viewRef.current.line : Math.max(0, Math.min(n - 1, cur + dir));
		const row = regionRef.current?.querySelector(`.rd-row[data-p="${p}"]`) as HTMLElement | null;
		const sb = stickyBottom();
		if (row) {
			const rect = row.getBoundingClientRect();
			if (rect.top >= sb && rect.bottom <= window.innerHeight) {
				row.focus({ preventScroll: true });
				say(ANNOUNCE.paragraph(fmtInt(p + 1), fmtInt(n)));
				return;
			}
		}
		arrive(p, { mark: false });
		say(ANNOUNCE.paragraph(fmtInt(p + 1), fmtInt(n)));
	};

	const commitG = () => {
		const st = gState.current;
		gState.current = null;
		if (!st) return;
		clearTimeout(st.timer);
		const num = Number(st.digits);
		if (!st.digits) return;
		if (num >= 1 && num <= vm.chapters.length) gotoChapter(num - 1);
		else say(ANNOUNCE.noChapter(st.digits));
	};

	const onKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Escape') {
			if (activeRef.current) {
				e.preventDefault();
				closeInspector();
			}
			return;
		}
		const region = regionRef.current;
		if (!region || !(e.target instanceof Node) || !region.contains(e.target)) return;
		if (e.ctrlKey || e.metaKey || e.altKey) return;
		if (e.key === 'Enter') {
			const cur = cursorRef.current;
			if (cur) {
				e.preventDefault();
				const pc = pieceAt(vm, cur.p, vm.spans.start[cur.span] as number, slotOfRef.current);
				const start = vm.spans.start[cur.span] as number;
				openInspector(cur.p, pc ? pc.a : start, pc ? pc.b : start + 1, true);
				// the inspector lists the spans at the cursor's own start
			} else {
				// no cursor yet: inspect the first mark of the focused paragraph
				const fp = focusedRowP();
				const first =
					fp === null ? null : region.querySelector<HTMLElement>(`.rd-row[data-p="${fp}"] mark`);
				if (fp !== null && first) {
					e.preventDefault();
					openInspector(fp, Number(first.dataset.a), Number(first.dataset.b), true);
				}
			}
			return;
		}
		if (!shortcutsRef.current) return;
		const gs = gState.current;
		if (gs) {
			if (/^\d$/.test(e.key)) {
				e.preventDefault();
				clearTimeout(gs.timer);
				gs.digits += e.key;
				gs.timer = window.setTimeout(commitG, 900);
				say(ANNOUNCE.gPending(gs.digits));
				return;
			}
			commitG();
		}
		switch (e.key) {
			case 'j':
				e.preventDefault();
				moveParagraph(1);
				break;
			case 'k':
				e.preventDefault();
				moveParagraph(-1);
				break;
			case 'n':
				e.preventDefault();
				stepEvidence(1);
				break;
			case 'N':
				e.preventDefault();
				stepEvidence(-1);
				break;
			case 'h':
				e.preventDefault();
				stepFlagged(1);
				break;
			case 'H':
				e.preventDefault();
				stepFlagged(-1);
				break;
			case 'g':
				e.preventDefault();
				gState.current = { digits: '', timer: window.setTimeout(commitG, 1500) };
				say(ANNOUNCE.gPending(''));
				break;
			default:
		}
	};

	/* ---- find in text (UX-MAP-15) ---- */
	const { find, findProp, findKey, runFind, stepFind } = useFind(
		vm,
		dv.paraOf,
		reveal,
		() => viewRef.current.line
	);

	/* ---- render ---- */
	const { segOf, fills } = segmentsOf(vm);
	const kept = keptBestParas.value;
	const rows = [];
	for (let p = win.start; p < win.end; p++) {
		let hsBits = 0;
		if (hs && p >= hs[0] && p < hs[1])
			hsBits = 1 | (p === hs[0] ? 2 : 0) | (p === hs[1] - 1 ? 4 : 0);
		rows.push(
			<Row
				key={p}
				vm={vm}
				p={p}
				slotOf={slotOf}
				layerKey={layerKey}
				activeA={active && active.p === p ? active.a : -1}
				cursorA={cursor && cursor.p === p ? cursor.a : -1}
				target={target?.p === p}
				hs={hsBits}
				cells={cellsByRow?.get(p)}
				segFill={fills[segOf[p] as number] ?? 'var(--ev-300)'}
				find={findProp}
				findKey={findKey}
				kept={kept.has(p)}
			/>
		);
	}
	const chapterIdx = vm.paras.chapter[view.line] ?? -1;
	const position = READER.position(fmtInt(view.v0 + 1), fmtInt(view.v1 + 1), fmtInt(n));
	const withheldFocused = focused.filter((id) => dv.withheld.includes(id));

	return (
		<div
			class={`rd${active ? ' has-inspector' : ''}`}
			ref={rootRef}
			onKeyDown={onKeyDown}
			data-testid="reader"
			data-layer={layer}
		>
			<h1 class="visually-hidden">{READER.title}</h1>
			<Toolbar
				vm={vm}
				layer={layer}
				onLayer={setLayer}
				onPrevEvidence={() => stepEvidence(-1)}
				onNextEvidence={() => stepEvidence(1)}
				onPrevFlagged={() => stepFlagged(-1)}
				onNextFlagged={() => stepFlagged(1)}
				chapter={chapterIdx}
				onChapter={gotoChapter}
				position={position}
				shortcuts={shortcuts}
				onShortcuts={(on) => {
					setShortcutsState(on);
					writePref('provenance.reader.shortcuts', on);
				}}
				open={toolsOpen}
				onOpen={setToolsOpen}
				find={find}
				onFind={runFind}
				onFindStep={stepFind}
			/>
			{layer === 'focused' && focused.length === 0 ? (
				<p class="rd-hint dim">{READER.noFocusHint}</p>
			) : null}
			{withheldFocused.length > 0 ? (
				<p class="rd-hint dim" data-testid="rd-withheld-hint">
					{READER.withheldHint(withheldFocused.join(', '))}
				</p>
			) : null}
			<div class="rd-grid">
				<ChapterRail vm={vm} current={chapterIdx} onPick={gotoChapter} />
				<div class="rd-main">
					{/* biome-ignore lint/a11y/useKeyWithClickEvents: marks have no tab stops; the evidence cursor and Enter are the keyboard path (UX-A11Y-05) */}
					<section
						class="rd-reader"
						aria-label={READER.region}
						// biome-ignore lint/a11y/noNoninteractiveTabindex: the region is the one keyboard entry to the reader (UX-A11Y-05)
						tabIndex={0}
						ref={regionRef}
						onClick={onClick}
						onPointerOver={onPointerOver}
						onPointerLeave={onPointerLeave}
						data-testid="rd-region"
						data-layer={layer}
						data-win={`${win.start}-${win.end}`}
					>
						<div class="rd-spacer" ref={topRef} aria-hidden="true" />
						{rows}
						<div class="rd-spacer" ref={botRef} aria-hidden="true" />
					</section>
					<div class="visually-hidden" aria-live="polite" data-testid="rd-live">
						{live}
					</div>
				</div>
				{active ? (
					<Inspector vm={vm} active={active} onClose={closeInspector} closeRef={closeRef} />
				) : null}
			</div>
			{tip ? (
				<div
					class="rd-tip"
					role="tooltip"
					style={{ left: tip.x + 14, top: tip.y + 16 }}
					data-testid="rd-tip"
				>
					{tip.text}
				</div>
			) : null}
		</div>
	);
}
